import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { EASE, scrollToId, RegTarget } from "@/components/atoms";
import { openCvRequest } from "@/components/CvRequest";

export const Hero = () => {
  const { t, lang } = useLanguage();
  const { scrollY } = useScroll();
  const yGhost = useTransform(scrollY, [0, 900], [0, 180]);
  const yMarks = useTransform(scrollY, [0, 900], [0, -70]);

  return (
    <section id="top" className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden px-5 md:px-10 pt-24 pb-20" data-testid="hero-section">
      <motion.span
        style={{ y: yGhost }}
        aria-hidden="true"
        className="pointer-events-none select-none absolute -right-4 md:right-6 top-1/2 -translate-y-1/2 font-serif italic font-light text-[26vw] md:text-[19vw] leading-none text-ink/[0.05] whitespace-nowrap"
      >
        {t.hero.ghost}
      </motion.span>

      <motion.div style={{ y: yMarks }} aria-hidden="true" className="pointer-events-none absolute inset-0">
        <RegTarget className="absolute left-[6%] top-[16%] w-8 h-8 text-ink/20" />
        <RegTarget className="absolute right-[10%] bottom-[18%] w-6 h-6 text-proof/30" />
      </motion.div>

      <div className="relative z-10 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
        >
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-proof" aria-hidden="true" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-proof" data-testid="hero-positioning">
              {t.hero.positioning}
            </span>
          </div>
          <h1 className="mt-5 font-serif font-light tracking-tight leading-none text-5xl md:text-7xl" data-testid="hero-name">
            {t.hero.name}
          </h1>
          <p className="mt-6 md:mt-8 font-serif italic text-xl md:text-3xl leading-snug max-w-3xl text-ink/90" data-testid="hero-seeking">
            {t.hero.seeking}
          </p>
          <p className="mt-4 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-smoke" data-testid="hero-availability">
            {t.hero.availability}
          </p>
        </motion.div>

        <div className="my-10 md:my-12 h-px bg-ink/15" aria-hidden="true" />

        <h2
          key={lang}
          className="font-serif font-light tracking-tighter leading-[1.02] text-[10.5vw] sm:text-6xl md:text-7xl lg:text-[5.2vw]"
          data-testid="hero-headline"
        >
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
              <motion.span
                className={`block ${i === 1 ? "italic" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.15, delay: 0.35 + i * 0.16, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-8 max-w-2xl text-lg md:text-xl font-light leading-relaxed text-ink/85"
          data-testid="hero-sub"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-cv"
            onClick={openCvRequest}
            className="group border border-ink bg-ink text-paper px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-transparent hover:text-ink"
          >
            {t.hero.ctaCv}
          </button>
          <button
            data-testid="hero-cta-contact"
            onClick={() => scrollToId("#kontakt")}
            className="border border-ink/30 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke transition-colors duration-500 hover:border-ink hover:text-ink"
          >
            {t.hero.ctaContact}
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-0 left-0 right-0 border-t border-hairline"
        data-testid="hero-meta-strip"
      >
        <div className="flex flex-wrap gap-x-8 gap-y-1 px-5 md:px-10 py-4">
          {t.hero.meta.map((m, i) => (
            <span key={i} className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-smoke">
              {m}
            </span>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
